import { getSubmitOrderBlockReason } from 'quotes/components/SubmitCustomerOrder/utils';
import { QuoteEntityType, QuoteStatus } from 'quotes/enums';
import { ICustomerQuote } from 'quotes/types';

type RefetchedQuote = Pick<ICustomerQuote, 'id' | 'status'>;

// Only the BE webhook subscriber writes these and a timed-out park never overwrites them, so a
// local one is newer than a refetched Submission Timed Out (its SSE event beat the refetch).
// Order Failed is deliberately absent: the BE parks from it too
// (OrderGuardService::RESUBMITTABLE_ORDER_STATUSES) and the park publishes no SSE event, so there
// the refetched status is the current one.
const WEBHOOK_RESOLVED_STATUSES = [QuoteStatus.ORDER_ORDERING, QuoteStatus.ORDER_ORDERED];

export const getQuoteAfterRefetch = <T extends RefetchedQuote>({
  current,
  refetched,
}: {
  current: T;
  refetched: T;
}): T =>
  current?.id === refetched?.id &&
  QuoteStatus.ORDER_SUBMISSION_TIMED_OUT === refetched?.status &&
  WEBHOOK_RESOLVED_STATUSES.includes(current?.status)
    ? current
    : refetched;

// Only /quote/details computes these flags; save responses replace the local quote without them.
export const withInactiveProjectLinkFlags = (
  current: ICustomerQuote | undefined,
  details?: Pick<ICustomerQuote, 'inactiveBillToProjectLink' | 'inactiveShipToProjectLink'>
): ICustomerQuote | undefined =>
  current && {
    ...current,
    inactiveBillToProjectLink: Boolean(details?.inactiveBillToProjectLink),
    inactiveShipToProjectLink: Boolean(details?.inactiveShipToProjectLink),
  };

const SUBMITTABLE_ORDER_STATUSES = [
  QuoteStatus.ORDER_DRAFT_VENDOR,
  QuoteStatus.ORDER_REQUESTED_BY_CUSTOMER,
  QuoteStatus.ORDER_ORDERING,
  QuoteStatus.ORDER_ORDER_FAILED,
  QuoteStatus.ORDER_SUBMISSION_TIMED_OUT,
];

export const isSubmitOrderVisible = (
  quoteEntityType: QuoteEntityType | undefined,
  status: QuoteStatus | undefined
): boolean =>
  QuoteEntityType.ORDER === quoteEntityType &&
  !!status &&
  SUBMITTABLE_ORDER_STATUSES.includes(status);

// The project block/warning only matter while a submit can still go out: hidden once the order is
// being processed, timed out or already known to the external system (it has an external number).
export const canOrderStillBeSubmitted = (
  quoteEntityType: QuoteEntityType | undefined,
  quote?: Pick<ICustomerQuote, 'status' | 'externalOrderNumber'> | null
): boolean =>
  isSubmitOrderVisible(quoteEntityType, quote?.status) && !getSubmitOrderBlockReason(quote);
